import { defineComponent, reactive, ref, SetupContext } from '@vue/composition-api';
import SystemConst from '@/shared/system-const';
import { TableHeader, PersonnelChangeResult, OnColumns, ConfigIconCoordinate } from '@/shared/type';
import vueRouter from '@/router/index';                                                     // ルート情報取得
import CommonOnColumnsMenu from '@/components/common/common-on-columns-menu.component.vue'; // 共通列設定メニューコンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';  // メッセージダイアログコンポーネント
import CommonFunction from '@/shared/common-function';                                      // 共通関数定義
import MessageConst from '@/shared/message-const';                                          // 共通メッセージ定義
import HttpStatus from 'http-status-codes';                                                 // HTTPステータスコード取得用
import { DataOptions } from 'vuetify';                                                      // v-data-tableのupdateイベントの引数を利用するための型
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';      // フィルタ用共通コンポーネント
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    isLoading: boolean;
    isError: boolean;
    personnelChangeResultItem: PersonnelChangeResult[];
    personnelChangeResultDisplayTableHeaders: TableHeader[];
    outputResultsDialog: boolean;
    isDownLoading: boolean;
    displayConfirmationDialog: boolean;
    pageOptions: DataOptions;
    selectItem: number;
    filterDefs: TableFilterDefs;
    filters: TableFilters;
    total: number;
};

/**
 * 年度更新（人事異動データ変換）チェック結果確認コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonOnColumnsMenu, CommonTableFilter },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.PERSONNEL_CHANGE_RESULT_LIST;    // テーブル高さ調整用変数
        const router = vueRouter;                                                     // ルータ
        const page = ref<number>(1);                                                  // ページ番号
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                         // 1ページあたりの行数
        const itemsPerPage = ref<number>(linesPerPage[1]);                            // 1ページあたりの行数（初期値）
        const sortBy = ref<string[]>([]);                                             // ソートカラム
        const sortDesc = ref<boolean[]>([]);                                          // ソート昇順、降順
        const tableHeaderClass: string[] = ['secondary subtitle-2'];                  // 人事異動データ変換エラー結果画面テーブルヘッダ色
        const child = ref<any>();                                                     // テーブルフィルタ一覧
        const commonRef = ref<any>(null);                                             // メッセージダイアログ
        const columnRef = ref<any>(null);                                             // 列設定    
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                      // テーブルのデータ取得中に表示するテキスト
        const stepper = <number>2;                                                    // ステッパーの2段階目
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            isLoading: false,                                                         // テーブルがロード中: true ロード中でない: false
            isError: false,                                                           // errorContentsにエラー内容がある: true エラー内容がない: false
            personnelChangeResultItem: [],                                            // テーブルのデータ
            personnelChangeResultDisplayTableHeaders: [],                             // 表示するテーブルヘッダー
            outputResultsDialog: false,                                               // 一覧データダウンロードダイアログ
            isDownLoading: false,                                                     // ファイルがダウンロード中: true ダウンロード中でない: false
            displayConfirmationDialog: false,                                         // Excel出力確認ダイアログ表示フラグ
            pageOptions: {                                                            // ページ情報・ソート情報
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            selectItem: 1,                                                            // モック用パラメータ
            filterDefs: {
                'status': { title: '正常 / エラー', types: 'CheckBox' },
                'lineNo': { title: '行番号', types: 'NumRange' },
                'processDivision': { title: '処理区分', types: 'CheckBox' },
                'identificationCode': { title: '識別コード', types: 'KeyWordPartialMatch' },
                'affiliationCode': { title: '所属コード', types: 'KeyWordPartialMatch' },
                'organizationName': { title: '組織名称', types: 'KeyWordPartialMatch' },
                'postName': { title: '職', types: 'KeyWordPartialMatch' },
                'lastName': { title: '姓', types: 'KeyWordPartialMatch' },
                'firstName': { title: '名', types: 'KeyWordPartialMatch' },
                'lastNameKana': { title: '姓（かな）', types: 'KeyWordPartialMatch' },
                'firstNameKana': { title: '名（かな）', types: 'KeyWordPartialMatch' },
                'errorContents': { title: 'エラー内容', types: 'KeyWordPartialMatch' }
            },
            filters: {},
            total: 0                                                                  // 一覧のデータ総件数
        });
        const errorIcon: TableHeader[] = [
            // エラーアイコンヘッダ
            {
                text: '',
                value: 'isError',
                width: '40px',
                class: tableHeaderClass,
                sortable: false
            }
        ];
        // チェック結果一覧ヘッダ情報(初期表示)
        const checkResultInitialDisplayTableHeaders: TableHeader[] = [
            // 行番号
            {
                text: '行番号',
                value: 'lineNo',
                width: '100px',
                class: tableHeaderClass
            },
            // 処理区分
            {
                text: '処理区分',
                value: 'processDivision',
                width: '140px',
                class: tableHeaderClass
            },
            // 識別コード
            {
                text: '識別コード',
                value: 'identificationCode',
                width: '140px',
                class: tableHeaderClass
            },
            // 組織名称
            {
                text: '組織名称',
                value: 'organizationName',
                width: '190px',
                class: tableHeaderClass
            },
            // 姓
            {
                text: '姓',
                value: 'lastName',
                width: '100px',
                class: tableHeaderClass
            },
            // 名
            {
                text: '名',
                value: 'firstName',
                width: '100px',
                class: tableHeaderClass
            },
            // エラー内容
            {
                text: 'エラー内容',
                value: 'errorContents',
                width: '400px',
                class: tableHeaderClass
            },
            // 行メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        // チェック結果一覧ヘッダ情報(全項目)
        const checkResultTableHeaders: TableHeader[] = [
            // 行番号
            {
                text: '行番号',
                value: 'lineNo',
                width: '100px',
                class: tableHeaderClass
            },
            // 処理区分
            {
                text: '処理区分',
                value: 'processDivision',
                width: '140px',
                class: tableHeaderClass
            },
            // 識別コード
            {
                text: '識別コード',
                value: 'identificationCode',
                width: '140px',
                class: tableHeaderClass
            },
            // 所属コード
            {
                text: '所属コード',
                value: 'affiliationCode',
                width: '190px',
                class: tableHeaderClass
            },
            // 組織名称
            {
                text: '組織名称',
                value: 'organizationName',
                width: '190px',
                class: tableHeaderClass
            },
            // 職
            {
                text: '職',
                value: 'postName',
                width: '140px',
                class: tableHeaderClass
            },
            // 姓
            {
                text: '姓',
                value: 'lastName',
                width: '100px',
                class: tableHeaderClass
            },
            // 名
            {
                text: '名',
                value: 'firstName',
                width: '100px',
                class: tableHeaderClass
            },
            // 姓（かな）
            {
                text: '姓（かな）',
                value: 'lastNameKana',
                width: '130px',
                class: tableHeaderClass
            },
            // 名（かな）
            {
                text: '名（かな）',
                value: 'firstNameKana',
                width: '130px',
                class: tableHeaderClass
            },
            // エラー内容
            {
                text: 'エラー内容',
                value: 'errorContents',
                width: '400px',
                class: tableHeaderClass
            },
            // 行メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        // 列設定情報
        const onColumns: OnColumns[] = [
            {
                text: '全て表示',
                value: false
            },
            // 行番号
            {
                text: '行番号',
                value: true
            },
            // 処理区分
            {
                text: '処理区分',
                value: true
            },
            // 識別コード
            {
                text: '識別コード',
                value: true
            },
            // 所属コード
            {
                text: '所属コード',
                value: false
            },
            // 組織名称
            {
                text: '組織名称',
                value: true
            },
            // 職
            {
                text: '職',
                value: false
            },
            // 姓
            {
                text: '姓',
                value: true
            },
            // 名
            {
                text: '名',
                value: true
            },
            // 姓（かな）
            {
                text: '姓（かな）',
                value: false
            },
            // 名（かな）
            {
                text: '名（かな）',
                value: false
            },
            // エラー内容
            {
                text: 'エラー内容',
                value: true
            }
        ];

        // モック用パラメータ
        const select = [
            {
                text: '正常',
                value: 1
            },
            {
                text: 'エラー',
                value: 2
            }
        ];

        // フィルターに設定された最大桁数
        const maxLength = {
            textLength: SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH,
            numRangeLength: SystemConst.FILTER_MAX_LENGTH.NUM_RANGE_LENGTH
        };

        // フィルタのバリデーションルール
        // 行番号
        const lineNoRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['numRangeLength'], '行番号')
        ];

        // 識別コード
        const identificationCodeRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '識別コード')
        ];

        // 所属コード
        const affiliationCodeRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '所属コード')
        ];

        // 組織名称
        const organizationNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '組織名称')
        ];

        // 職
        const postNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '職')
        ];

        // 姓
        const lastNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '姓')
        ];

        // 名
        const firstNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '名')
        ];

        // 姓（かな）
        const lastNameKanaRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '姓（かな）')
        ];

        // 名（かな）
        const firstNameKanaRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '名（かな）')
        ];

        // エラー内容
        const errorContentsRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], 'エラー内容')
        ];

        /**
         * フィルタ初期設定
         * 
         * フィルタにDBから取得する必要のある条件がある場合、初期表示で取得する。
         */
        async function initFilter() {
            // フィルタ条件の内容を取得
            // 人事異動変換ストアのgetFilterConditionsを呼び出す
            const response = await ctx.root.$store.dispatch('personnelChange/getFilterConditions');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラーメッセージダイアログを表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // フィルタ（プルダウン・チェックボックス）に表示するデータを設定する
            // 正常 / エラー
            state.filterDefs['status'].chkItems = response.data.status;
            // 処理区分
            state.filterDefs['processDivision'].chkItems = response.data.processDivision;

            // バリデーションを設定する
            // 行番号
            state.filterDefs['lineNo'].startValidator = lineNoRules;
            state.filterDefs['lineNo'].endValidator = lineNoRules;
            // 識別コード
            state.filterDefs['identificationCode'].validator = identificationCodeRules;
            // 所属コード
            state.filterDefs['affiliationCode'].validator = affiliationCodeRules;
            // 組織名称
            state.filterDefs['organizationName'].validator = organizationNameRules;
            // 職
            state.filterDefs['postName'].validator = postNameRules;
            // 姓
            state.filterDefs['lastName'].validator = lastNameRules;
            // 名
            state.filterDefs['firstName'].validator = firstNameRules;
            // 姓（かな）
            state.filterDefs['lastNameKana'].validator = lastNameKanaRules;
            // 名（かな）
            state.filterDefs['firstNameKana'].validator = firstNameKanaRules;
            // エラー内容
            state.filterDefs['errorContents'].validator = errorContentsRules;
        }

        /**
         * created
         */
        created();

        /**
         * 人事異動データ変換エラー結果画面初期表示処理
         */
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-download-outline');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '人事異動データ変換');

            // 初期表示ヘッダ設定
            state.personnelChangeResultDisplayTableHeaders = errorIcon.concat(checkResultInitialDisplayTableHeaders);

            // 一覧情報をクリアする
            state.personnelChangeResultItem = [];

            // フィルタの初期表示（完了するまでawaitで待つ）
            await initFilter();

            // チェック結果一覧情報取得処理を行う
            updateList();
        }

        /**
         * フィルタ設定によるイベント
         * 
         * フィルタの条件を追加・削除したタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            state.filters = filters;

            // 1ページへ戻す
            state.pageOptions.page = 1;
            page.value = 1;

            // 人事異動変換一覧更新
            updateList();
        }

        /**
         * 一覧のページ／ソート操作によるイベント
         * 
         * @param options ページ／ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ/ソート情報をstate内へ保存する
            state.pageOptions = options;

            if (page.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // データの中身を比較して、いずれも変更されていなかった場合、
                // ページの更新を行わない
                return;
            }

            // ページ情報を設定する
            page.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソート情報を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // チェック結果一覧を表示更新する
            updateList();
        }

        /**
         * チェック結果一覧の表示更新
         */
        async function updateList() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // state内の変数を参照し、以下からAPIのパラメータを作成する。
            //   ・ページ数
            //   ・１ページあたりの行数
            //   ・ソートキー
            //   ・ソート順
            //   ・フィルタ情報
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // チェック結果一覧取得APIでデータを取得する
            const response = await ctx.root.$store.dispatch('personnelChange/getResults',
                {
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する。
                state.isLoading = false;

                // HTTPステータスコードが404の場合、
                // エラーメッセージを表示してファイル選択画面へ遷移する。
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 404エラーのみダイアログを表示する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/fiscal-year-update/personnel-change/upload');
                } else {
                    // HTTPステータスコードが404以外はエラーダイアログを表示、又はエラー画面へ遷移する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // テーブル情報を更新する
            state.personnelChangeResultItem = response.data.checkResults;
            // 総データ件数の更新を行う
            state.total = response.data.total;
            // エラーの有無を判別するパラメータを取得する
            state.isError = response.data.isError;
            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * 登録ボタン押下時、ダイアログ表示処理
         */
        function displayDialog() {
            // 確認メッセージを設定する
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['変換処理']);

            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
            // 「はい」 をクリックすると、processingData()を呼び出す
            // 「いいえ」 をクリックするとダイアログを閉じる
        }

        /**
         * 確認メッセージダイアログにて「はい」を押下した際の処理
         * backサイドで一括登録ファイルを作成する
         * 処理終了後、step3のファイルダウンロード画面へ遷移する
         */
        async function processingData() {
            // 一括登録ファイル作成処理のAPIを実行する
            const response = await ctx.root.$store.dispatch('personnelChange/createBulkFile');

            // HTTPステータスコードが404の場合、
            // エラーメッセージを表示してファイル選択画面へ遷移する。
            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する。
                state.isLoading = false;

                // HTTPステータスコードが404の場合、
                // エラーメッセージを表示してファイル選択画面へ遷移する。
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 404エラーのみダイアログを表示する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/fiscal-year-update/personnel-change/upload');
                } else {
                    // HTTPステータスコードが404以外はエラーダイアログを表示、又はエラー画面へ遷移する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // ステータスコード200の場合
            // 画面遷移処理を行う
            router.push('/fiscal-year-update/personnel-change/download');
        }

        /**
         * 戻るボタン押下処理
         * ファイル選択画面へ戻る
         */
        function back(): void {
            router.push('/fiscal-year-update/personnel-change/upload');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 列設定アイコンをクリックした際に発火する
         * 列設定アイコンの座標を取得し、列設定メニューコンポーネントのshowMenuを発火する
         */
        function showMenu() {
            // 列設定アイコンの座標を取得
            const configIconCoordinate: ConfigIconCoordinate = { x: null, y: null };
            configIconCoordinate.x = document.querySelector('#column');
            configIconCoordinate.y = document.querySelector('#column');

            // 列設定メニューコンポーネントのshowMenuを発火
            columnRef.value.showMenu(configIconCoordinate);
        }

        /**
         * 列設定メニューで、列設定情報が更新された際に発火する
         * 設定後のテーブルヘッダー情報を取得する
         * 
         * @param displayTableHeaders 画面に表示するテーブルヘッダー情報
         */
        function getTableHeader(displayTableHeaders: TableHeader[]) {
            // ヘッダー情報の更新
            state.personnelChangeResultDisplayTableHeaders = errorIcon.concat(displayTableHeaders);
        }

        /**
         * Excelファイルのダウンロード
         */
        async function outputExcelFile() {
            // 確認ダイアログを非表示にする
            state.displayConfirmationDialog = false;

            // ファイルダウンロード中のローダーを表示する
            state.isDownLoading = true;

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // ExcelファイルダウンロードAPIを呼び出す
            // パラメータ: フィルタ・ソート情報
            const response = await ctx.root.$store.dispatch('personnelChange/outputExcelFile',
                {
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // ファイルダウンロード中のローダーを非表示にする
            state.isDownLoading = false;
            state.outputResultsDialog = false;

            // API通信が正常か判別
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合はエラーダイアログを表示、又はエラー画面へ遷移する
                commonRef.value.errorDisplayTransitionControl(response);
            }
        }

        return {
            tableHeight,
            stepper,
            state,
            page,
            linesPerPage,
            itemsPerPage,
            sortBy,
            sortDesc,
            child,
            commonRef,
            columnRef,
            tableLoadingText,
            checkResultInitialDisplayTableHeaders,
            checkResultTableHeaders,
            onColumns,
            select,
            processingData,
            back,
            displayDialog,
            updateOptions,
            onResize,
            showMenu,
            getTableHeader,
            updateFilter,
            outputExcelFile
        }
    }
});
